import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { Sparkles, TrendingUp, Award, Loader2 } from 'lucide-react';
import caseMembersService from '../../services/caseMembersService';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AddMemberModal Component
 * Modal for adding a team member to a case
 * 
 * Props:
 *   - caseId: Case UUID
 *   - existingMembers: Array of current members (to exclude from list)
 *   - onAdd: Callback (userId, role) => void
 *   - onClose: Callback to close modal
 */
const AddMemberModal = ({ caseId, existingMembers, onAdd, onClose }) => {
  const { t } = useTranslation('team');
  const [lawyers, setLawyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState('ASSISTANT');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  const roles = [
    { value: 'ASSISTANT', label: t('roles.assistant'), description: t('roles.assistantDesc') },
    { value: 'PARALEGAL', label: t('roles.paralegal'), description: t('roles.paralegalDesc') },
    { value: 'EXPERT', label: t('roles.expert'), description: t('roles.expertDesc') },
    { value: 'EXTERNAL', label: t('roles.external'), description: t('roles.externalDesc') }
  ];

  useEffect(() => {
    fetchLawyers();
    fetchAISuggestions();
  }, []);

  const fetchLawyers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      // Fetch all users from the tenant
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Include all users except CLIENT role
      const allUsers = response.data.filter(u => u.role !== 'CLIENT');
      
      // Exclude existing members
      const existingIds = existingMembers.map(m => m.user_id);
      const available = allUsers.filter(user => !existingIds.includes(user.id));
      
      setLawyers(available);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error(t('addMember.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchAISuggestions = async () => {
    try {
      setLoadingSuggestions(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/capacity/suggest/case/${caseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter out existing members
      const existingIds = existingMembers.map(m => m.user_id);
      const availableSuggestions = (response.data.suggestions || []).filter(
        s => !existingIds.includes(s.user_id)
      );
      
      setSuggestions(availableSuggestions);
    } catch (error) {
      console.error('Failed to fetch AI suggestions:', error);
      // Don't show error - suggestions are optional
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!selectedUserId) {
      toast.error(t('addMember.selectPersonError'));
      return;
    }

    onAdd(selectedUserId, selectedRole);
  };

  const filteredLawyers = lawyers.filter(lawyer =>
    lawyer.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    lawyer.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getRoleLabel = (role) => {
    const labels = {
      'TENANT_ADMIN': t('roles.admin'),
      'ADMIN': t('roles.admin'),
      'LAWYER': t('roles.lawyer'),
      'USER': t('roles.user'),
      'PARALEGAL': t('roles.paralegal'),
      'ASSISTANT': t('roles.assistant')
    };
    return labels[role] || role;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            {t('addMember.title')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* AI Suggestions Toggle */}
          {suggestions.length > 0 && (
            <div className="flex items-center justify-between p-3 bg-gray-50 border border-indigo-200 rounded-lg">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-indigo-600" />
                <div>
                  <p className="text-sm font-semibold text-indigo-900">
                    {t('suggestions.available')}
                  </p>
                  <p className="text-xs text-indigo-700">
                    {suggestions.length} {t('suggestions.matchingFound')}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowSuggestions(!showSuggestions)}
                className="px-3 py-1 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm"
              >
                {showSuggestions ? t('suggestions.hide') : t('suggestions.show')}
              </button>
            </div>
          )}

          {/* AI Suggestions Panel */}
          {showSuggestions && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-indigo-600" />
                <h3 className="text-sm font-semibold text-gray-900">
                  {t('suggestions.title')}
                </h3>
              </div>
              
              {loadingSuggestions ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                </div>
              ) : (
                <div className="space-y-2">
                  {suggestions.slice(0, 5).map((suggestion, idx) => (
                    <div
                      key={suggestion.user_id}
                      onClick={() => {
                        setSelectedUserId(suggestion.user_id);
                        setShowSuggestions(false);
                      }}
                      className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                        selectedUserId === suggestion.user_id
                          ? 'border-indigo-500 bg-gray-50'
                          : 'border-gray-200 hover:border-indigo-300 bg-white'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-bold text-indigo-600">#{idx + 1}</span>
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{suggestion.name}</p>
                            <p className="text-xs text-gray-600">{suggestion.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-xs font-semibold text-indigo-700">
                          <Award className="w-3 h-3" />
                          Score: {suggestion.score}
                        </div>
                      </div>
                      
                      {/* Status & Workload */}
                      <div className="flex items-center gap-2 mb-2 text-xs">
                        <span className={`px-2 py-0.5 rounded font-medium ${
                          suggestion.status === 'AVAILABLE' ? 'bg-green-100 text-green-700' :
                          suggestion.status === 'NORMAL' ? 'bg-blue-100 text-blue-700' :
                          suggestion.status === 'BUSY' ? 'bg-orange-100 text-orange-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {suggestion.status === 'AVAILABLE' ? t('status.available') :
                           suggestion.status === 'NORMAL' ? t('status.normal') :
                           suggestion.status === 'BUSY' ? t('status.busy') : t('status.overloaded')}
                        </span>
                        <span className="text-gray-600">
                          {suggestion.utilization_percent}% {t('status.utilization')}
                        </span>
                        <span className="text-gray-600">•</span>
                        <span className="text-gray-600">
                          {suggestion.active_cases} {t('status.cases')}
                        </span>
                      </div>
                      
                      {/* Reasons */}
                      <div className="space-y-1">
                        {suggestion.reasons?.map((reason, rIdx) => (
                          <div key={rIdx} className="flex items-start gap-1 text-xs text-gray-700">
                            <TrendingUp className="w-3 h-3 text-gray-600 mt-0.5 flex-shrink-0" />
                            <span>{reason}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Divider */}
          {showSuggestions && <div className="border-t border-gray-200" />}

          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {showSuggestions 
                ? t('addMember.searchLabelAlt') 
                : t('addMember.searchLabel')}
            </label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('addMember.searchPlaceholder')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500"
            />
          </div>

          {/* User Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('addMember.selectPerson')} ({filteredLawyers.length} {t('addMember.available')})
            </label>
            
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : filteredLawyers.length === 0 ? (
              <p className="text-gray-500 text-center py-4">
                {t('addMember.noMembers')}
              </p>
            ) : (
              <div className="border border-gray-300 rounded-lg divide-y divide-gray-200 max-h-64 overflow-y-auto">
                {filteredLawyers.map((lawyer) => (
                  <label
                    key={lawyer.id}
                    className={`flex items-center p-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                      selectedUserId === lawyer.id ? 'bg-gray-50' : ''
                    }`}
                  >
                    <input
                      type="radio"
                      name="lawyer"
                      value={lawyer.id}
                      checked={selectedUserId === lawyer.id}
                      onChange={(e) => setSelectedUserId(e.target.value)}
                      className="mr-3"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{lawyer.name}</p>
                      <p className="text-xs text-gray-500">{lawyer.email}</p>
                    </div>
                    <span className="text-xs text-gray-500 px-2 py-1 bg-gray-100 rounded">
                      {getRoleLabel(lawyer.role)}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Role Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('addMember.selectRole')}
            </label>
            <div className="space-y-2">
              {roles.map((role) => (
                <label
                  key={role.value}
                  className={`flex items-start p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedRole === role.value
                      ? 'border-indigo-500 bg-gray-50'
                      : 'border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="role"
                    value={role.value}
                    checked={selectedRole === role.value}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    className="mt-1 mr-3"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{role.label}</p>
                    <p className="text-xs text-gray-500">{role.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t('addMember.cancelButton')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedUserId}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {t('addMember.addButton')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddMemberModal;
